/**
 * Where every address the flat step editor minted now goes.
 *
 * Bookmarks, notification rows already sent, and governance emails all carry
 * `edit/<step>` and `view/<step>`. Those addresses are permanent in the sense
 * that matters: they exist outside this application and nothing can rewrite
 * them, so the routes stay and forward.
 *
 * @module router/programme-editor/legacy-redirects
 */

import { SURFACE_INDEX, SURFACES } from "./surfaces";

export interface LegacyRedirect {
  /** The old path, relative to a programme base. */
  from: string;
  /** The new path, relative to the same base. */
  to: string;
}

/** The legacy keys that lived under `view/` rather than `edit/`. */
const VIEW_KEYS: ReadonlySet<string> = new Set([
  "module-view",
  "schedule",
  "traceability",
  "snapshot",
]);

/**
 * The module editor tab a legacy step key now opens.
 *
 * The studio surface collapsed five legacy steps into one section with a
 * blank segment (surfaces.ts), so the redirect target alone no longer says
 * which tab an old bookmark meant. `module-view` named the whole module list
 * rather than one tab, so it is not here and lands on the bare surface.
 */
const LEGACY_KEY_MODULE_TAB: Readonly<Record<string, string>> = {
  mimlos: "outcomes",
  "effort-hours": "effort",
  assessments: "assessments",
  "reading-lists": "reading",
};

/**
 * Every legacy address, derived from the surface registry rather than listed
 * again: a section that changes address changes here with it, which is what
 * stops the table drifting from the routes it forwards into.
 */
export function legacyRedirects(): readonly LegacyRedirect[] {
  const table: LegacyRedirect[] = [];

  SURFACES.forEach((surface) => {
    surface.sections.forEach((section) => {
      const to = section.segment ? `${surface.segment}/${section.segment}` : `${surface.segment}`;
      section.stateKeys.forEach((key) => {
        // The store calls the module view `module-view`; its address was
        // `view/modules`, which is the one place the two disagree.
        const segment = key === "module-view" ? "modules" : key;
        const prefix = VIEW_KEYS.has(key) ? "view" : "edit";
        const tab = LEGACY_KEY_MODULE_TAB[key];
        table.push({ from: `${prefix}/${segment}`, to: tab ? `${to}?tab=${tab}` : to });
      });
    });
  });

  // `edit` alone was the editor's own front door, and the bare programme
  // address forwards to it, so both land on the overview.
  table.push({ from: "edit", to: `overview/${SURFACE_INDEX.overview}` });

  return table;
}
